import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { useCreateTipoAtivo } from '@/hooks/useTiposAtivos';
import { TipoAtivoFormData } from '@/types/tipo-ativo';
import { IconSelector } from './IconSelector';
import { ColorPicker } from './ColorPicker';
import { CamposPadraoSelector } from './CamposPadraoSelector';

const tipoAtivoSchema = z.object({
  nome: z.string().min(1, 'Nome é obrigatório'),
  descricao: z.string().optional(),
  icone: z.string().optional(),
  cor: z.string().optional(),
  categoria: z.string().optional(),
  campos_padrao: z.object({
    basico: z.array(z.string()).optional(),
    tecnico: z.array(z.string()).optional(),
    negocio: z.array(z.string()).optional(),
    seguranca: z.array(z.string()).optional(),
  }).optional(),
});

interface NewTipoAtivoDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const NewTipoAtivoDialog = ({ open, onOpenChange }: NewTipoAtivoDialogProps) => {
  const createMutation = useCreateTipoAtivo();

  const form = useForm<TipoAtivoFormData>({
    resolver: zodResolver(tipoAtivoSchema),
    defaultValues: {
      nome: '',
      descricao: '',
      icone: 'Package',
      cor: '#3b82f6',
      categoria: '',
      campos_padrao: {
        basico: ['nome', 'tipo_id'],
        tecnico: [],
        negocio: ['client_id'],
        seguranca: [],
      },
    },
  });

  const onSubmit = async (data: TipoAtivoFormData) => {
    await createMutation.mutateAsync(data);
    form.reset();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Novo Tipo de Ativo</DialogTitle>
          <DialogDescription>
            Configure um novo tipo de ativo com campos específicos
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="nome"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nome *</FormLabel>
                    <FormControl>
                      <Input placeholder="Ex: Servidor" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="categoria"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Categoria</FormLabel>
                    <FormControl>
                      <Input placeholder="Ex: Hardware" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="descricao"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Descrição</FormLabel>
                  <FormControl>
                    <Textarea placeholder="Descrição do tipo de ativo" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="icone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Ícone</FormLabel>
                    <FormControl>
                      <IconSelector value={field.value} onChange={field.onChange} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="cor"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Cor</FormLabel>
                    <FormControl>
                      <ColorPicker value={field.value} onChange={field.onChange} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="campos_padrao"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Campos Padrão</FormLabel>
                  <FormDescription>
                    Selecione quais campos padrão devem aparecer para este tipo
                  </FormDescription>
                  <FormControl>
                    <CamposPadraoSelector value={field.value} onChange={field.onChange} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={createMutation.isPending}>
                {createMutation.isPending ? 'Criando...' : 'Criar Tipo'}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
